import { useTranslation } from 'react-i18next';
import { Container, Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Section, AnimatedBox } from '../../components/ui';

const PageHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.background.hero,
  padding: theme.spacing(16, 0, 8),
  position: 'relative',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(12, 0, 6),
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),

  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },

  '& p': {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
  },
}));

export default function Privacy() {
  const { t } = useTranslation();
  const lastUpdated = 'January 2024';

  return (
    <>
      <PageHeader>
        <Container maxWidth="lg">
          <AnimatedBox animation="fadeInUp">
            <Typography variant="h1" sx={{ color: 'text.light', mb: 2 }}>
              Privacy Policy
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Last updated: {lastUpdated}
            </Typography>
          </AnimatedBox>
        </Container>
      </PageHeader>

      <Section>
        <Container maxWidth="md">
          <AnimatedBox animation="fadeInUp">
            <ContentSection>
              <Typography variant="h2" component="h2">1. Introduction</Typography>
              <Typography component="p">
                Nexfah ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </Typography>
              <Typography component="p">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">2. Information We Collect</Typography>
              <Typography component="p">
                We may collect information about you in a variety of ways. The information we may collect includes:
              </Typography>
              <Typography component="p">
                <strong>Personal Data:</strong> Personally identifiable information, such as your name, email address, telephone number, and company name that you voluntarily give to us when you contact us or register for our services.
              </Typography>
              <Typography component="p">
                <strong>Derivative Data:</strong> Information our servers automatically collect when you access the site, such as your IP address, browser type, operating system, access times, and the pages you have viewed.
              </Typography>
              <Typography component="p">
                <strong>Financial Data:</strong> Financial information, such as data related to your payment method (e.g., valid credit card number, card brand, expiration date) that we may collect when you purchase our services.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">3. Use of Your Information</Typography>
              <Typography component="p">
                Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. We may use information collected about you to:
              </Typography>
              <List>
                <ListItem><ListItemText primary="Create and manage your account" /></ListItem>
                <ListItem><ListItemText primary="Process transactions and send related information" /></ListItem>
                <ListItem><ListItemText primary="Send you administrative information, updates, and security alerts" /></ListItem>
                <ListItem><ListItemText primary="Respond to your comments, questions, and requests" /></ListItem>
                <ListItem><ListItemText primary="Improve our website and services" /></ListItem>
                <ListItem><ListItemText primary="Monitor and analyze usage and trends" /></ListItem>
                <ListItem><ListItemText primary="Comply with legal obligations" /></ListItem>
              </List>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">4. Disclosure of Your Information</Typography>
              <Typography component="p">
                We may share information we have collected about you in certain situations:
              </Typography>
              <Typography component="p">
                <strong>By Law or to Protect Rights:</strong> If we believe the release of information is necessary to respond to legal process, investigate potential violations, or protect the rights, property, and safety of others.
              </Typography>
              <Typography component="p">
                <strong>Business Transfers:</strong> We may share or transfer your information in connection with a merger, acquisition, or sale of all or a portion of our assets.
              </Typography>
              <Typography component="p">
                <strong>Third-Party Service Providers:</strong> We may share your information with third parties that perform services for us, including payment processing, data analysis, email delivery, and customer service.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">5. Security of Your Information</Typography>
              <Typography component="p">
                We use administrative, technical, and physical security measures to protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that no security measures are perfect, and no method of data transmission can be guaranteed against interception or misuse.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">6. Data Retention</Typography>
              <Typography component="p">
                We will retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements. To determine the appropriate retention period, we consider the amount, nature, and sensitivity of the data.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">7. Your Rights (GDPR)</Typography>
              <Typography component="p">
                If you are a resident of the European Economic Area (EEA), you have certain data protection rights:
              </Typography>
              <List>
                <ListItem><ListItemText primary="The right to access, update, or delete your information" /></ListItem>
                <ListItem><ListItemText primary="The right of rectification" /></ListItem>
                <ListItem><ListItemText primary="The right to object to processing" /></ListItem>
                <ListItem><ListItemText primary="The right of restriction" /></ListItem>
                <ListItem><ListItemText primary="The right to data portability" /></ListItem>
                <ListItem><ListItemText primary="The right to withdraw consent" /></ListItem>
              </List>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">8. Contact Us</Typography>
              <Typography component="p">
                If you have questions or comments about this Privacy Policy, please contact us at:
              </Typography>
              <Typography component="p">
                <strong>Nexfah</strong><br />
                Email: info@nexfah.com<br />
                Phone: +44 7888 601455<br />
                London, United Kingdom
              </Typography>
            </ContentSection>
          </AnimatedBox>
        </Container>
      </Section>
    </>
  );
}
